"use client";

import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { defaultNavConfig, defaultThemeConfig, MODE_STORAGE_KEY, navBgVar, navElementOrder, navRadiusVar, THEME_CACHE_KEY, themeTokenOrder } from "@/lib/theme";
import { getThemePreset, readThemePreviewFlag, resolvePreset, writeThemePreviewFlag } from "@/lib/theme-presets";
import { NavBarStyle, NavElementKey, ThemeConfig, ThemeMode, ThemeTokenKey } from "@/lib/types";

type ThemeContextValue = {
  mode: ThemeMode;
  theme: ThemeConfig;
  isLoading: boolean;
  isSaving: boolean;
  saveError: string | null;
  /** Взведённый пресет-кандидат (?themePreview=/window.name) или null. */
  previewName: string | null;
  /** Применён ли сейчас кандидат (пилюля «Текущая ⇄ Кандидат»). */
  previewActive: boolean;
  setMode: (mode: ThemeMode) => void;
  setPreviewActive: (active: boolean) => void;
  clearPreview: () => void;
  updateToken: (mode: ThemeMode, token: ThemeTokenKey, value: string) => void;
  updateNav: (element: NavElementKey, mode: ThemeMode, patch: Partial<NavBarStyle>) => void;
  updateNavRadius: (element: NavElementKey, radius: number) => void;
  updateRadius: (radius: number) => void;
  updateFontSize: (size: number) => void;
  updateLogoUrl: (mode: ThemeMode, url: string) => void;
  resetTheme: () => void;
  saveTheme: () => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyTheme(mode: ThemeMode, theme: ThemeConfig, cache: boolean) {
  const root = document.documentElement;
  // Пока меняем переменные — переходы выключены (см. .theme-switching в
  // globals.css): иначе каждый элемент анимирует свой цвет сам, и на экране с
  // большой таблицей перекраска идёт видимой волной в несколько секунд.
  // Снимаем через два кадра: первый — применение стилей, второй — отрисовка.
  root.classList.add("theme-switching");
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove("theme-switching"));
  });
  root.dataset.theme = mode;
  for (const token of themeTokenOrder) {
    root.style.setProperty(`--${token}`, theme[mode][token]);
  }
  root.style.setProperty("--card", theme[mode]["surface"]);
  // ⚠️ --hover НЕ РАВЕН surfaceElevated (Максим, 26.08.2026).
  // Был равен — и в светлой теме подсветка строки не работала вовсе: surface
  // там #ffffff, surfaceElevated #faf9ff, разница на глаз нулевая. А на этом
  // токене держится подсветка строки в 46 местах портала: списки, выпадашки,
  // таблицы. То есть «строки не выделяются» было не в одном списке SKU, а
  // почти везде, просто в тёмной теме разница чуть заметнее.
  //
  // Теперь это ПРИМЕСЬ ЧЕРНИЛ К ПОВЕРХНОСТИ: 10 % цвета текста поверх surface.
  //
  // ⚠️ Почему 10, а не 7. Часть мест берёт токен с прозрачностью —
  // `bg-[var(--hover)]/60` — и срезает его ещё почти вдвое. При 7 % на выходе
  // оставалось около четырёх пунктов из 255: подсветка формально была, а глазом
  // не читалась (Максим, 26.08.2026, список выбора SKU). 10 % переживают такое
  // ослабление и остаются видимыми.
  // Такой тон всегда контрастен фону, на котором лежит, потому что берётся от
  // противоположного конца палитры — на светлой теме темнит, на тёмной светлит.
  // ⚠️ 02.09.2026: формула сведена к той, которой подсвечивается карточка на
  // доске задач — 12 % ПРИГЛУШЁННОГО текста поверх surface (Максим: «сделать
  // такое же серое выделение строк, как в доске задач, и применить ко всему
  // порталу»). Было 10 % основного текста: чуть жёстче и холоднее. Токен один
  // на 46 мест — списки, выпадашки, таблицы, строки файлов и подзадач.
  root.style.setProperty(
    "--hover",
    `color-mix(in srgb, ${theme[mode]["textMuted"]} 12%, ${theme[mode]["surface"]})`,
  );
  root.style.setProperty("--radius-lg", `${theme.radius}px`);
  // --radius-md — производный «средний» радиус (используется в полях, бейджах,
  // меню, карточках). Раньше нигде не задавался → border-radius был невалиден
  // и элементы получались прямоугольными.
  //
  // ⚠️ ПОЛ 6, А НЕ 8. При общем радиусе 8 формула max(8, radius − 6) упиралась
  // в собственный минимум и возвращала те же 8: элементы переставали
  // уменьшаться вместе с темой, и панель с полем внутри имели ОДИНАКОВОЕ
  // скругление — вложенность переставала читаться. На радиусе 18 (прежние
  // сохранённые темы) результат не изменился: 12, как и было.
  root.style.setProperty("--radius-md", `${Math.max(6, theme.radius - 6)}px`);
  root.style.fontSize = `${theme.baseFontSize ?? 15}px`;
  // Матовые панели навигации: --nav-top-bg / --nav-side-bg / --nav-aside-bg.
  const nav = theme.nav ?? defaultNavConfig;
  for (const el of navElementOrder) {
    const cfg = nav[el] ?? defaultNavConfig[el];
    root.style.setProperty(`--nav-${el}-bg`, navBgVar(cfg[mode]));
    root.style.setProperty(`--nav-${el}-radius`, navRadiusVar(cfg.radius, el));
  }
  // Кэш для инлайн-скрипта в layout.tsx: следующая загрузка страницы применит
  // эту же тему ДО первой отрисовки (без флэша дефолтной палитры).
  // В режиме превью кэш НЕ пишем: localStorage общий для всех вкладок/iframe,
  // кандидатская тема отравила бы пре-пейнт настоящего портала.
  if (!cache) return;
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
      light: theme.light,
      dark: theme.dark,
      radius: theme.radius,
      baseFontSize: theme.baseFontSize ?? 15,
      nav: theme.nav ?? defaultNavConfig,
    }));
  } catch { /* quota/private mode — некритично */ }
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>("light");
  const [theme, setTheme] = useState<ThemeConfig>(defaultThemeConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Превью темы-кандидата (/theme-compare): имя валидного пресета или null.
  const [previewName, setPreviewName] = useState<string | null>(() =>
    typeof window === "undefined" ? null : (getThemePreset(readThemePreviewFlag())?.name ?? null),
  );
  const [previewActive, setPreviewActive] = useState(true);

  useEffect(() => {
    const savedMode = typeof window !== "undefined" ? localStorage.getItem(MODE_STORAGE_KEY) : null;
    if (savedMode === "light" || savedMode === "dark") {
      setModeState(savedMode);
    }
  }, []);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const response = await api.getTheme();
        setTheme(response);
      } catch {
        setTheme(defaultThemeConfig);
      } finally {
        setIsLoading(false);
      }
    };

    void loadTheme();
  }, []);

  // window.name переживает перезагрузки → превью не слетает при hard reload.
  useEffect(() => {
    writeThemePreviewFlag(previewName);
  }, [previewName]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const preset = getThemePreset(previewName);
    const effective = previewActive && preset ? resolvePreset(theme, preset) : theme;
    // Пока превью взведено (даже выключенное пилюлей) — кэш темы не пишем.
    applyTheme(mode, effective, previewName === null);
    const root = document.documentElement;
    if (previewActive && preset) {
      root.dataset.skin = preset.name;
    } else {
      delete root.dataset.skin;
    }
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode, theme, previewName, previewActive]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      theme,
      isLoading,
      isSaving,
      saveError,
      previewName,
      previewActive,
      setMode: setModeState,
      setPreviewActive,
      clearPreview: () => setPreviewName(null),
      updateToken: (targetMode, token, value) => {
        setTheme((prev) => ({
          ...prev,
          [targetMode]: {
            ...prev[targetMode],
            [token]: value,
          },
        }));
      },
      updateNav: (element, targetMode, patch) =>
        setTheme((prev) => {
          const base = prev.nav ?? defaultNavConfig;
          return {
            ...prev,
            nav: {
              ...base,
              [element]: {
                ...base[element],
                [targetMode]: { ...base[element][targetMode], ...patch },
              },
            },
          };
        }),
      updateNavRadius: (element, radius) =>
        setTheme((prev) => {
          const base = prev.nav ?? defaultNavConfig;
          return {
            ...prev,
            nav: { ...base, [element]: { ...base[element], radius } },
          };
        }),
      updateRadius: (radius) => setTheme((prev) => ({ ...prev, radius })),
      updateFontSize: (size) => setTheme((prev) => ({ ...prev, baseFontSize: size })),
      updateLogoUrl: (logoMode, url) =>
        setTheme((prev) => ({
          ...prev,
          ...(logoMode === "light" ? { logoUrlLight: url } : { logoUrlDark: url }),
        })),
      resetTheme: () => setTheme(defaultThemeConfig),
      saveTheme: async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
          const saved = await api.saveTheme(theme);
          setTheme(saved);
        } catch (error) {
          setSaveError(error instanceof Error ? error.message : "Не удалось сохранить тему.");
          throw error;
        } finally {
          setIsSaving(false);
        }
      },
    }),
    [mode, theme, isLoading, isSaving, saveError, previewName, previewActive],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
